import { Grid } from '@mui/material';
import { isDef, isDefAndNotNull } from '@utils/def';
import useDeepCallback from '@utils/useDeepCallback';
import { FC, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import AutoComplete, {
  MultipleAutocompleteResponse,
  OptionType,
  SingleAutocompleteResponse,
  Sizes,
} from '../../../AutoComplete';
import FormField from '../../../FormField';

import { FormFieldTypes } from '../../enums';
import {
  FormFieldOption,
  FormFieldSelect,
  FormFieldSelectValueType,
  FormFieldUnion,
  FormFieldValue,
  FormFields,
} from '../../types';
import {
  extractValue,
  getGridColumnAmount,
  getValidationRulesFromAField,
} from '../../utils';

import SchemaFormRenderer from '../SchemaFormRenderer';

import { AutoCompleteContainer } from './styles';
import { Props } from './types';

const FormSelectRenderer: FC<Props> = ({ field }) => {
  const { control, resetField } = useFormContext<FormFields>();
  const selectFieldValue = useWatch({ name: field.name });
  const validationRules = useMemo(
    () => getValidationRulesFromAField(field),
    [field],
  );
  const isMultiple = useMemo(() => !!field?.multiple, [field.multiple]);
  const gridColumnAmount = useMemo(
    () => getGridColumnAmount(field?.columnsAmount),
    [field.columnsAmount],
  );
  const selectedDependantFieldProps: Partial<FormFieldSelect> = useMemo(() => {
    const fieldValue = extractValue(selectFieldValue || field.defaultValue);
    const selectedField = field?.dependantFields?.find(
      (f) => f?.value === fieldValue,
    ) as FormFieldSelect;
    return isDef(selectedField)
      ? {
          offsetColumnsAmount: selectedField.offsetColumnsAmount,
          columnsAmount: selectedField.columnsAmount,
        }
      : {};
  }, [selectFieldValue, field.defaultValue, field?.dependantFields]);
  const gridOffsetColumnAmount = useMemo(
    () =>
      isDef(field.offsetColumnsAmount)
        ? getGridColumnAmount(field.offsetColumnsAmount)
        : isDef(selectedDependantFieldProps.offsetColumnsAmount)
          ? getGridColumnAmount(selectedDependantFieldProps.offsetColumnsAmount)
          : undefined,
    [
      field.offsetColumnsAmount,
      selectedDependantFieldProps.offsetColumnsAmount,
    ],
  );

  const dependantFields: FormFieldUnion[] = useMemo(() => {
    const fieldValue = extractValue(selectFieldValue || field.defaultValue);

    return (
      field?.dependantFields?.find((f) => f?.value === fieldValue)
        ?.dependantFields || []
    );
  }, [selectFieldValue, field.defaultValue, field?.dependantFields]);
  const optionFields: FormFieldOption[] = useMemo(
    () =>
      (field.dependantFields || []).filter(
        (p) => p.type === FormFieldTypes.option,
      ) as FormFieldOption[],
    [field.dependantFields],
  );
  const defaultOptions: OptionType[] | undefined = useMemo(() => {
    return optionFields.length
      ? (optionFields.map((p) => ({
          label: p.label || '',
          value: p.value,
        })) as OptionType[])
      : field.options;
  }, [optionFields, field]);

  const clearDependantFieldsDelayed = useCallback(
    (value: FormFieldValue) => {
      requestIdleCallback(() => {
        optionFields.forEach((option) => {
          const isActiveVariant = option.value === value;
          (option.dependantFields || []).forEach((f) => {
            if (isActiveVariant && isDefAndNotNull(f.defaultValue)) {
              resetField(f.name, { defaultValue: f.defaultValue });
            } else {
              resetField(f.name, { defaultValue: null });
            }
          });
        });
      });
    },
    [optionFields, resetField],
  );

  const handleSelectChange = useDeepCallback(
    (
      onChange: (
        value: SingleAutocompleteResponse | MultipleAutocompleteResponse,
      ) => void,
      value: SingleAutocompleteResponse | MultipleAutocompleteResponse,
    ) => {
      clearDependantFieldsDelayed(extractValue(value) as FormFieldValue);
      onChange?.(value);
    },
  );

  return (
    <>
      <Grid item xs={gridColumnAmount}>
        <FormField
          {...validationRules}
          defaultValue={field.defaultValue}
          controllerProps={{
            control,
            name: field.name,
            rules: field.validate ? { validate: field.validate } : undefined,
          }}
          render={(props) => (
            <AutoCompleteContainer size={field.size as unknown as Sizes}>
              <AutoComplete<OptionType, typeof isMultiple>
                {...props}
                size={field.size as unknown as Sizes}
                disabled={field.disabled}
                error={props.error}
                getOptionLabel={field.getOptionLabel}
                label={field.label}
                loading={field.optionsLoading}
                multiple={field?.multiple}
                name={field.name}
                onChange={handleSelectChange(
                  (
                    value:
                      | SingleAutocompleteResponse
                      | MultipleAutocompleteResponse,
                  ) => props.onChange(value),
                )}
                onChangeCallback={field.onChangeCallback}
                onInputChange={field.onInputChange}
                options={defaultOptions}
                optionsWithCheckbox={field.optionsWithCheckbox}
                disableCloseOnSelect={field.disableCloseOnSelect}
                placeholder={field.placeholder}
                readOnly={field.readOnly}
                required={field.required}
                showLabel={field.showLabel}
                value={
                  field.value
                    ? (field.value as FormFieldSelectValueType)
                    : (props.value as FormFieldSelectValueType)
                }
                postfix={field.postfix}
              />
            </AutoCompleteContainer>
          )}
        />
      </Grid>
      {isDef(gridOffsetColumnAmount) && (
        <Grid item xs={gridOffsetColumnAmount} />
      )}
      <SchemaFormRenderer fields={dependantFields} />
    </>
  );
};

export default FormSelectRenderer;
